import type { OptimizationGroupReviewModel } from '@/shared/generated'

import React, { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { find } from 'lodash/fp'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import {
	useCreateReviewMutation,
	useDeleteReviewMutation,
	useOptimizationReviewsQuery,
	useUpdateReviewMutation,
} from '@/entities/optimization-review'
import {
	useAllUsersQuery,
	useCurrentUserQuery,
	usersToOptions,
} from '@/entities/user'

import {
	FormProvider,
	RHFCheckbox,
	RHFMultiSelect,
} from '@/shared/components/HookForm'
import { Button } from '@/shared/components/ui/button'
import { confirm } from '@/shared/components/ui/confirmer'
import { Loader } from '@/shared/components/ui/loader'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { ampli } from '@/shared/lib/ampli'
import {
	actCreated,
	actDeleted,
	actUpdated,
	successActionMessage,
} from '@/shared/tools/message'

const formatReviewersBeforeSend = (
	ids: string[],
	review?: OptimizationGroupReviewModel
) =>
	ids.map(
		(id) => review?.reviewers?.find((reviewer) => reviewer.id === id) ?? { id }
	)

type ReviewManagerModalProps = {
	isOpen: boolean
	close: () => void
	optGroupId: string
}

const FormDataSchema = z.object({
	reviewers: z.array(z.string()).min(1, 'Reviewers is required'),
	notify_author: z.boolean(),
})
type FormDataType = z.infer<typeof FormDataSchema>

export function OptimizationReviewModal({
	isOpen,
	close,
	optGroupId,
}: ReviewManagerModalProps) {
	const { t } = useTranslation()

	useEffect(() => {
		ampli.approvalOpenReviewManagerModal()
	}, [])
	const { data: review, isLoading } = useOptimizationReviewsQuery<
		OptimizationGroupReviewModel | undefined
	>({
		select: find<OptimizationGroupReviewModel>({
			optimization_group_id: optGroupId,
		}),
	})
	const users = useAllUsersQuery()
	const sessionUser = useCurrentUserQuery()
	const createReview = useCreateReviewMutation()
	const updateReview = useUpdateReviewMutation()
	const deleteReview = useDeleteReviewMutation()
	const methods = useForm<FormDataType>({
		defaultValues: review
			? {
					reviewers: review.reviewers.map(({ id }) => id),
					notify_author: review.notify_author,
				}
			: {
					reviewers: [],
					notify_author: false,
				},
		resolver: zodResolver(FormDataSchema),
	})
	useEffect(() => {
		if (review) {
			methods.reset({
				reviewers: review.reviewers.map(({ id }) => id),
				notify_author: review.notify_author,
			})
		}
		// eslint-disable-next-line @eslint-react/exhaustive-deps
	}, [review])
	const values = methods.watch()
	const usersData = (users?.data ?? []).filter(
		({ apps, id }) =>
			find(
				{ name: 'pricing_platform' },
				apps
			)?.entities?.optimization_group?.includes(optGroupId) &&
			id !== sessionUser?.data?.id
	)
	const optionsUsers = usersToOptions(usersData)
	const isSessionUserOwner = review
		? review?.author_id === sessionUser?.data?.id
		: true
	const handleDelete = async () => {
		const answer = await confirm({
			title: t('general_detete_confirm_title'),
			description: t('general_detete_confirm_subtitle'),
			actionText: t('general_delete', {
				ns: 'keys',
			}),
			cancelText: t('general_cancel', {
				ns: 'keys',
			}),
			ActionProps: {
				variant: 'destructive',
			},
		})

		if (answer) {
			deleteReview.mutateAsync(optGroupId).then(() => {
				toast.success(successActionMessage('Deleted', actDeleted(t), t))
				close()
			})
		}
	}
	const onSubmit = (data: FormDataType) => {
		const payload = {
			optimizationGroupId: optGroupId,
			body: {
				...data,
				reviewers: formatReviewersBeforeSend(data.reviewers, review),
			},
		}
		if (review) {
			updateReview.mutateAsync(payload).then(() => {
				toast.success(successActionMessage('Review', actUpdated(t), t))
				close()
			})
		} else {
			ampli.approvalCreateReview({
				'Reviewers count': data.reviewers?.length,
				'Notify author': data.notify_author,
			})
			createReview.mutateAsync(payload).then(() => {
				toast.success(successActionMessage('Review', actCreated(t), t))
				close()
			})
		}
	}

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent>
				<FormProvider
					methods={methods}
					onSubmit={methods.handleSubmit(onSubmit)}
					className='flex flex-col gap-3'
				>
					<ModalHeader>
						<ModalTitle>{t('review_manager_title')}</ModalTitle>
						<ModalDescription>{t('review_manager_subtitle')}</ModalDescription>
					</ModalHeader>
					<ModalBody className='flex flex-col gap-3'>
						{isLoading && <Loader />}
						<RHFMultiSelect
							isLoading={users.isLoading}
							label={t('reviewers')}
							name='reviewers'
							options={optionsUsers || []}
						/>
						<RHFCheckbox
							label={t('review_manager_field_notify')}
							name='notify_author'
						/>
					</ModalBody>
					<ModalFooter className='justify-between space-x-2'>
						<ModalClose asChild>
							<Button>{t('general_cancel')}</Button>
						</ModalClose>
						{review && isSessionUserOwner && values.reviewers.length === 0 ? (
							<Button variant='primary' onClick={handleDelete}>
								{t('discard_review')}
							</Button>
						) : (
							<Button
								variant='primary'
								disabled={values.reviewers.length === 0 || isLoading}
								onClick={methods.handleSubmit(onSubmit)}
							>
								{t('general_save')}
							</Button>
						)}
					</ModalFooter>
				</FormProvider>
			</ModalContent>
		</Modal>
	)
}
